import type * as React from "react";

import { cn } from "../cn";

// Chart UX pass 2026-10-21 (Rippling / ChartHop / Pingboard): the org chart's root list. In the
// `tree` view the cards hang top-down under their parents with hairline connectors (org-tree.css,
// pseudo-elements on the nested `ul` / `li`); in the `list` view the same nested lists read as an
// indented list and the caller indents each level. The caller renders `<li>`s with a card and,
// for a branch, a nested `<ul>` of its children. `zoom` scales the tree view (CSS `zoom`, so the
// scroll container sees the scaled size and pointer geometry stays right): the caller fits the
// tree to its canvas or lets the viewer zoom.

export type OrgTreeProps = React.ComponentProps<"ul"> & {
  view?: "tree" | "list";
  /** Scale of the tree view, 1 = 100%. Ignored by the list view. */
  zoom?: number;
  /**
   * W9-FIXES: hang each group of three or more leaf siblings in two columns either side of a
   * stem (org-tree.css), so a wide, shallow tree uses the canvas's height. Tree view only; the
   * caller turns it on when it lets the tree fit larger.
   */
  compact?: boolean;
};

export function OrgTree({
  view = "tree",
  zoom,
  compact,
  className,
  style,
  ...props
}: OrgTreeProps) {
  const scaled = view === "tree" && zoom !== undefined && zoom !== 1;
  return (
    <ul
      data-slot="org-tree"
      data-org-tree={view}
      data-compact={view === "tree" && compact ? "" : undefined}
      data-zoom={scaled ? zoom.toFixed(2) : undefined}
      className={cn(view === "list" && "flex min-w-0 flex-col", className)}
      style={scaled ? { ...style, zoom } : style}
      {...props}
    />
  );
}
